import type { ReactNode } from 'react'
import cx from 'classnames'
import { StyledLink } from '@/components/Link'
import type { PressEventHandler } from '@/infra/prop-types'
import styles from './ProgressionBar.module.css'

interface ProgressionBarProps {
  children?: ReactNode
  testId?: string
}

/**
 * Progression Bar root.
 * Holds the progression bar subcomponents.
 */
export function ProgressionBar({ children, testId }: ProgressionBarProps) {
  return (
    <div className={styles.progressionBar} data-test={testId}>
      <div className={styles.progressionBarContainer}>{children}</div>
    </div>
  )
}

interface ProgressionBarStepProps {
  children?: ReactNode
  testId?: string
}

/**
 * Progression Bar Step.
 * Displays the number of the current step in the progression relative to the total number of steps in the progression happy path.
 */
export function ProgressionBarStep({
  children,
  testId,
}: ProgressionBarStepProps) {
  return (
    <div className={styles.progressionBarStep} data-test={testId}>
      <span className={styles.progressionBarText}>{children}</span>
    </div>
  )
}

interface ProgressionBarIndicatorProps {
  currentStep: number
  totalSteps: number
  testId?: string
}

/**
 * Progression Bar Indicator.
 * Displays a graphical representation of progress along the progression happy path.
 */
export function ProgressionBarIndicator({
  currentStep,
  totalSteps,
  testId,
}: ProgressionBarIndicatorProps) {
  return (
    <div className={styles.progressionBarIndicator} data-test={testId}>
      {new Array(currentStep).fill(null).map((_, index) => (
        <div
          className={cx(styles.progressionBarIndicatorItem, styles.complete)}
          key={index}
        />
      ))}
      {new Array(totalSteps - currentStep).fill(null).map((_, index) => (
        <div
          className={cx(styles.progressionBarIndicatorItem, styles.incomplete)}
          key={index}
        />
      ))}
    </div>
  )
}

interface ProgressionBarNextStepProps {
  children?: ReactNode
  title?: ReactNode
  testId?: string
}

/**
 * Progression Bar Next Step.
 * Holds the link to see all progression steps, and displays the next step title if there is one.
 */
export function ProgressionBarNextStep({
  children,
  title,
  testId,
}: ProgressionBarNextStepProps) {
  return (
    <div className={styles.progressionBarNextStep} data-test={testId}>
      {title && (
        <>
          <span className={styles.progressionBarText}>
            Up next:{' '}
            <span className={styles.progressionBarSubtext}>{title}</span>
          </span>
          {children && (
            <div className={styles.progressionBarNextStepSeparator} />
          )}
        </>
      )}
      {children}
    </div>
  )
}

interface ProgressionBarLinkProps {
  children?: ReactNode
  onPress?: PressEventHandler
  testId?: string
}

/**
 * Progression Bar Link.
 * Displays the link to see all progression steps.
 */
export function ProgressionBarLink({
  children,
  onPress,
  testId,
}: ProgressionBarLinkProps) {
  return (
    <StyledLink variant="inherit" onPress={onPress} testId={testId}>
      <span className={styles.progressionBarLinkContent}>{children}</span>
    </StyledLink>
  )
}
